import React, { useCallback } from "react";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { computeComboAlert } from "@/features/betslip/utils/alerts";
import { InlineAlert } from "@/features/betslip/components/parts/InlineAlert";
import { useShareEntryModal } from "@/features/entries/hooks/useShareEntryModal";
import { ShareEntryAction } from "@/features/entries/components/share/ShareEntryAction";
import { useEntry } from "hooks/firestore/betting/useEntries";
import type { Alert as AlertType } from "@/features/betslip/types";

type ComboAlertProps = {
  type: string;
  count: number;
};

export const ComboAlert = ({ type, count }: ComboAlertProps) => {
  const combination = useBetslipStore((s) => s.stakes.combinations[type]);
  const setCombinationStake = useBetslipStore((s) => s.setCombinationStake);
  const dismissCombinationResult = useBetslipStore(
    (s) => s.dismissCombinationResult,
  );
  const acceptMultiOddsChanges = useBetslipStore(
    (s) => s.acceptMultiOddsChanges,
  );
  const setOverlay = useBetslipStore((s) => s.setOverlay);
  const [setShareOpen, getShareModalProps] = useShareEntryModal();

  // Each combination places as its own bet, so share rides on the
  // combination's own betId once it's accepted.
  const shareReferenceId =
    combination?.betId && combination.entryId !== undefined
      ? combination.betId
      : undefined;
  const { entry, entryLoading } = useEntry({ entryId: shareReferenceId ?? "" });

  const handleCta = useCallback(
    (cta: NonNullable<AlertType["cta"]>) => {
      if (!combination) return;
      switch (cta.action) {
        case "updateComboStake":
          {
            const payload = cta.payload as
              | { alternativeStake?: number }
              | undefined;
            if (payload?.alternativeStake !== undefined) {
              setCombinationStake(type, {
                ...combination,
                total: payload.alternativeStake,
                perCombo: Math.floor(payload.alternativeStake / count),
                rejected: undefined,
              });
            }
          }
          break;
        case "dismissCombo":
          dismissCombinationResult(type);
          break;
        case "acceptOdds":
          // Combos price off the multi legs, so the rejection clears by
          // taking those legs' pending prices.
          acceptMultiOddsChanges();
          dismissCombinationResult(type);
          break;
        case "deposit":
          setOverlay({ type: "deposit" });
          break;
        case "share":
          setShareOpen(true);
          break;
      }
    },
    [
      combination,
      setCombinationStake,
      dismissCombinationResult,
      acceptMultiOddsChanges,
      setOverlay,
      setShareOpen,
      type,
      count,
    ],
  );

  if (!combination) return null;

  const alert = computeComboAlert(combination, !!entry);

  return (
    <>
      <InlineAlert alert={alert} placement="card" onCta={handleCta} />
      {shareReferenceId && (
        <ShareEntryAction
          {...getShareModalProps({
            entry: entry ? [entry, entryLoading] : undefined,
            referenceId: shareReferenceId,
          })}
        />
      )}
    </>
  );
};
